import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { QueueInfo, QueueReminderState } from '@/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import EarlyAccessPilotBadge from '@/components/environment/EarlyAccessPilotBadge';
import VideoDebugOverlay from '@/components/teleop/VideoDebugOverlay';
import LiveKitOperatorProvider from '@/providers/LiveKitOperatorProvider';
import { isVideoDebugEnabledFromLocation } from '@/lib/videoDebug';
import { EARLY_ACCESS_PILOT_MESSAGE, isEarlyAccessPilotEnvironment } from '@/lib/earlyAccessPilot';
import { TELEOP_SETTINGS } from '@/app/Teleop/teleopConfig';
import {
  adaptManualFrameStyleToViewport,
  computeCriticalVideoStyle,
  computeManualVideoStyle,
  isCompatibilityVideoFramingBrowser,
  isFirefoxBrowser,
  selectCriticalBoundingBox,
  selectViewportProfile,
} from '@/lib/videoFraming';
import type { PwaInstallState } from '@/lib/pwa';
import type { PushSetupState } from '@/hooks/usePushSetup';

type QueueScreenProps = {
  mode: 'loading' | 'queued' | 'offered' | 'error';
  queueInfo?: QueueInfo | null;
  offerExpiresAt?: string | null;
  errorMessage?: string | null;
  queueReminderState?: QueueReminderState | null;
  queueReminderBusy?: boolean;
  queueReminderErrorMessage?: string | null;
  onRetry?: () => void;
  onConfirmReady?: () => void;
  onAcknowledge?: () => void;
  onRequestInstall?: () => Promise<unknown>;
  onEnablePushSetup?: () => Promise<void>;
  onDisablePushSetup?: () => Promise<void>;
  onEnableQueueReminder?: () => Promise<void>;
  onDisableQueueReminder?: () => Promise<void>;
  pwaInstallState?: PwaInstallState | null;
  pushSetupState?: PushSetupState | null;
  roomId?: string | null;
  compact?: boolean;
};

type InstallSheetCopy = {
  title: string;
  body: string;
  steps: string[];
  primaryActionLabel: string | null;
};

type PushActionKind = 'enable' | 'disable' | null;

type PushCalloutCopy = {
  title: string;
  body: string;
  launcherLabel: string | null;
};

type PushSheetCopy = {
  title: string;
  body: string;
  steps: string[];
  primaryActionLabel: string | null;
  primaryActionKind: PushActionKind;
};

type QueueReminderActionKind = 'enable' | 'disable' | null;

type QueueReminderCalloutCopy = {
  title: string;
  body: string;
  launcherLabel: string | null;
};

type QueueReminderSheetCopy = {
  title: string;
  body: string;
  steps: string[];
  primaryActionLabel: string | null;
  primaryActionKind: QueueReminderActionKind;
};

const formatEta = (etaSeconds?: number) => {
  if (etaSeconds === undefined || etaSeconds === null) {
    return 'Calculating';
  }
  if (etaSeconds < 60) {
    return `${etaSeconds}s`;
  }
  const minutes = Math.ceil(etaSeconds / 60);
  return `${minutes} min`;
};

const parseOfferExpiryMs = (offerExpiresAt?: string | null): number | null => {
  if (!offerExpiresAt) {
    return null;
  }

  const parsedValue = Date.parse(offerExpiresAt);
  if (Number.isNaN(parsedValue)) {
    return null;
  }

  return parsedValue;
};

const formatOfferCountdownLabel = (remainingSeconds: number): string => {
  if (remainingSeconds <= 0) {
    return 'right now';
  }

  if (remainingSeconds < 60) {
    if (remainingSeconds === 1) {
      return '1 second';
    }

    return `${remainingSeconds} seconds`;
  }

  const remainingMinutes = Math.ceil(remainingSeconds / 60);
  if (remainingMinutes === 1) {
    return 'about 1 minute';
  }

  return `about ${remainingMinutes} minutes`;
};

const buildOfferedSubtitle = (
  offerExpiresAt: string | null | undefined,
  nowMs: number,
): string => {
  const offerExpiryMs = parseOfferExpiryMs(offerExpiresAt);
  if (offerExpiryMs === null) {
    return "You're up next. Click I'm Ready before your offer expires or you'll get skipped.";
  }

  const remainingSeconds = Math.max(0, Math.ceil((offerExpiryMs - nowMs) / 1000));
  const countdownLabel = formatOfferCountdownLabel(remainingSeconds);
  if (countdownLabel === 'right now') {
    return "You're up next. Click I'm Ready right now or you'll get skipped.";
  }

  return `You're up next, you have ${countdownLabel} to click I'm Ready or you'll get skipped.`;
};

const buildInstallSummary = (installState: PwaInstallState): string => {
  if (installState.installMethod === 'native_prompt') {
    return "Install Play Claw to your home screen so this device can alert you when it's almost your turn.";
  }

  return "Add Play Claw to your iPhone Home Screen so this device can alert you when it's almost your turn.";
};

const buildInstallSheetCopy = (installState: PwaInstallState): InstallSheetCopy => {
  if (installState.installMethod === 'native_prompt') {
    return {
      title: 'Add to Home Screen for alerts',
      body: "Queue alerts work best from the installed app. Add Play Claw now, then open it from your home screen when you want alerts on this device.",
      steps: [
        'Tap Continue.',
        'Accept the browser install prompt.',
        'Open Play Claw from your home screen to finish alert setup.',
      ],
      primaryActionLabel: 'Continue',
    };
  }

  return {
    title: 'Add to Home Screen for alerts',
    body: "On iPhone, queue alerts only work from the Home Screen app. Add Play Claw to your Home Screen. If you've already added it, open it from your Home Screen instead of Safari.",
    steps: [
      'Open the Share menu in Safari.',
      'Tap Add to Home Screen.',
      'Open Play Claw from your Home Screen to use alerts on this device.',
    ],
    primaryActionLabel: null,
  };
};

const getInstallLauncherLabel = (installState: PwaInstallState): string => {
  if (installState.installMethod === 'native_prompt') {
    return 'Set Up Alerts';
  }

  return 'See How';
};

const getCompactInstallLauncherLabel = (installState: PwaInstallState): string => {
  if (installState.installMethod === 'native_prompt') {
    return 'Set Up';
  }

  return 'How';
};

const buildPushCalloutCopy = (pushSetupState: PushSetupState): PushCalloutCopy => {
  if (pushSetupState.isLoading) {
    return {
      title: 'Checking queue alerts',
      body: 'Checking whether this installed app is already registered.',
      launcherLabel: null,
    };
  }

  if (
    pushSetupState.currentDeviceSubscription &&
    pushSetupState.permissionState === 'granted'
  ) {
    return {
      title: 'Queue alerts ready',
      body: 'This installed app is registered and ready for the queue-alert rollout.',
      launcherLabel: 'Manage',
    };
  }

  if (pushSetupState.permissionState === 'denied') {
    return {
      title: 'Notifications blocked',
      body: 'Turn notifications back on for Play Claw to use queue alerts on this device.',
      launcherLabel: 'View Steps',
    };
  }

  return {
    title: 'Set up queue alerts',
    body: 'Register this installed app now so this device is ready for queue reminders.',
    launcherLabel: 'Enable Alerts',
  };
};

const buildPushSheetCopy = (pushSetupState: PushSetupState): PushSheetCopy => {
  if (
    pushSetupState.currentDeviceSubscription &&
    pushSetupState.permissionState === 'granted'
  ) {
    return {
      title: 'Queue alerts ready',
      body: 'This device is registered for queue alerts. Use the queue reminder control for the current ticket.',
      steps: [
        'Keep notifications allowed for Play Claw on this device.',
        'Open Play Claw from your home screen when you want the fastest return path.',
        'Use the queue reminder control for this ticket when you want alerts here.',
      ],
      primaryActionLabel: 'Turn Off Device Setup',
      primaryActionKind: 'disable',
    };
  }

  if (pushSetupState.permissionState === 'denied') {
    return {
      title: 'Turn notifications back on',
      body: 'Notifications are blocked for Play Claw on this device. Re-enable them in browser settings, then return here and enable queue alerts again.',
      steps: [
        'Open this browser or site settings for Play Claw.',
        'Allow notifications for Play Claw.',
        'Return here and tap Enable Queue Alerts.',
      ],
      primaryActionLabel: pushSetupState.currentDeviceSubscription
        ? 'Turn Off Device Setup'
        : null,
      primaryActionKind: pushSetupState.currentDeviceSubscription
        ? 'disable'
        : null,
    };
  }

  return {
    title: 'Enable queue alerts',
    body: 'Register this installed app now so this device can be selected for queue reminders. This step sets up the device only.',
    steps: [
      'Allow notifications when the browser prompts you.',
      'Keep Play Claw installed on this device.',
      'Use the queue reminder control for this ticket after device setup completes.',
    ],
    primaryActionLabel: 'Enable Queue Alerts',
    primaryActionKind: 'enable',
  };
};

const getCompactPushLauncherLabel = (pushSetupState: PushSetupState): string => {
  if (pushSetupState.isLoading) {
    return '...';
  }

  if (
    pushSetupState.currentDeviceSubscription &&
    pushSetupState.permissionState === 'granted'
  ) {
    return 'Manage';
  }

  if (pushSetupState.permissionState === 'denied') {
    return 'Fix';
  }

  return 'Enable';
};

type BuildQueueReminderCopyInput = {
  queueReminderState: QueueReminderState | null;
  currentDeviceSubscriptionId: string;
};

const buildQueueReminderCalloutCopy = ({
  queueReminderState,
  currentDeviceSubscriptionId,
}: BuildQueueReminderCopyInput): QueueReminderCalloutCopy => {
  if (queueReminderState === null) {
    return {
      title: 'Checking reminder state',
      body: 'Loading the reminder state for this queue ticket.',
      launcherLabel: null,
    };
  }

  const reminderTargetsCurrentDevice =
    queueReminderState.requested &&
    queueReminderState.subscription_id === currentDeviceSubscriptionId;
  if (reminderTargetsCurrentDevice) {
    return {
      title: 'Queue reminder on',
      body: "This ticket is set to alert this device when it's almost ready and when you're up.",
      launcherLabel: 'Manage',
    };
  }

  if (queueReminderState.requested) {
    return {
      title: 'Reminder set on another device',
      body: 'Turn it on here if you want this installed app to receive the queue alerts instead.',
      launcherLabel: 'Use This Device',
    };
  }

  return {
    title: "Notify me when it's almost ready",
    body: 'Use this installed app for almost-ready and offered alerts for this ticket.',
    launcherLabel: 'Turn On',
  };
};

const buildQueueReminderSheetCopy = ({
  queueReminderState,
  currentDeviceSubscriptionId,
}: BuildQueueReminderCopyInput): QueueReminderSheetCopy => {
  if (queueReminderState === null) {
    return {
      title: 'Checking reminder state',
      body: 'Loading the current reminder state for this ticket.',
      steps: ['Please wait a moment.'],
      primaryActionLabel: null,
      primaryActionKind: null,
    };
  }

  const reminderTargetsCurrentDevice =
    queueReminderState.requested &&
    queueReminderState.subscription_id === currentDeviceSubscriptionId;
  if (reminderTargetsCurrentDevice) {
    return {
      title: 'Queue reminder on',
      body: "This ticket will use this device for almost-ready and offered alerts.",
      steps: [
        'Keep notifications allowed for Play Claw on this device.',
        'Keep Play Claw installed on your home screen.',
        'Open the app from the notification for the fastest return path.',
      ],
      primaryActionLabel: 'Turn Off Reminder',
      primaryActionKind: 'disable',
    };
  }

  if (queueReminderState.requested) {
    return {
      title: 'Use this device instead',
      body: 'This ticket is already set to notify another device. Turn reminders on here to move alerts to this installed app.',
      steps: [
        'Leave notifications enabled for Play Claw on this device.',
        'Turn on the reminder here to replace the other device.',
        'Use the notification to jump back into Play Claw when the queue moves.',
      ],
      primaryActionLabel: 'Use This Device',
      primaryActionKind: 'enable',
    };
  }

  return {
    title: 'Turn on queue reminder',
    body: "We'll use this device for almost-ready and offered alerts for this ticket.",
    steps: [
      'Leave notifications enabled for Play Claw on this device.',
      'Turn on the reminder for this ticket.',
      'Use the notification to jump back into Play Claw when the queue moves.',
    ],
    primaryActionLabel: 'Turn On Reminder',
    primaryActionKind: 'enable',
  };
};

const getCompactQueueReminderLauncherLabel = ({
  queueReminderState,
  currentDeviceSubscriptionId,
}: BuildQueueReminderCopyInput): string => {
  if (queueReminderState === null) {
    return '...';
  }

  const reminderTargetsCurrentDevice =
    queueReminderState.requested &&
    queueReminderState.subscription_id === currentDeviceSubscriptionId;
  if (reminderTargetsCurrentDevice) {
    return 'On';
  }

  if (queueReminderState.requested) {
    return 'Use Here';
  }

  return 'Notify Me';
};

const {
  video: {
    source: {
      widthPx: SOURCE_VIDEO_WIDTH_PX,
      heightPx: SOURCE_VIDEO_HEIGHT_PX,
    },
    viewport: {
      mobileMaxWidthPx: MOBILE_MAX_WIDTH_PX,
      tabletMaxWidthPx: TABLET_MAX_WIDTH_PX,
      cameraProfiles: {
        overhead: OVERHEAD_VIDEO_VIEWPORT,
      },
    },
  },
} = TELEOP_SETTINGS;

const QueueScreen: React.FC<QueueScreenProps> = ({
  mode,
  queueInfo,
  offerExpiresAt,
  errorMessage,
  queueReminderState,
  queueReminderBusy,
  queueReminderErrorMessage,
  onRetry,
  onConfirmReady,
  onAcknowledge,
  onRequestInstall,
  onEnablePushSetup,
  onDisablePushSetup,
  onEnableQueueReminder,
  onDisableQueueReminder,
  pwaInstallState,
  pushSetupState,
  roomId,
  compact,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const shouldShowEarlyAccessPilot = isEarlyAccessPilotEnvironment(import.meta.env.MODE);
  const [offerCountdownNowMs, setOfferCountdownNowMs] = useState<number>(() => Date.now());
  const title =
    mode === 'loading'
      ? 'Finding a robot'
      : mode === 'queued'
        ? 'Get In Line?'
        : mode === 'offered'
          ? "You're up next"
          : 'Something went wrong';
  const subtitle =
    mode === 'loading'
      ? 'We are matching you with the next available robot.'
      : mode === 'queued'
        ? 'Keep this screen open. We will move you in when a robot is free.'
        : mode === 'offered'
          ? buildOfferedSubtitle(offerExpiresAt, offerCountdownNowMs)
          : 'We could not start a session right now.';
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [livekitStatus, setLivekitStatus] = useState<'idle' | 'connecting' | 'connected' | 'failed'>('idle');
  const [isInstallSheetOpen, setIsInstallSheetOpen] = useState(false);
  const [isPushSheetOpen, setIsPushSheetOpen] = useState(false);
  const [isQueueReminderSheetOpen, setIsQueueReminderSheetOpen] = useState(false);
  const [viewportSize, setViewportSize] = useState<{ widthPx: number; heightPx: number }>({
    widthPx: window.innerWidth,
    heightPx: window.innerHeight,
  });
  const [isFirefox] = useState<boolean>(() => isFirefoxBrowser());
  const [usesCompatibilityVideoFraming] = useState<boolean>(() =>
    isCompatibilityVideoFramingBrowser()
  );
  const [videoDebugEnabled] = useState<boolean>(() => isVideoDebugEnabledFromLocation());
  const isCompact = Boolean(compact && (mode === 'queued' || mode === 'offered'));
  const installState = pwaInstallState ?? null;
  const pushState = pushSetupState ?? null;
  const isQueuedMode = mode === 'queued';
  const shouldShowInstallCta =
    isQueuedMode &&
    installState !== null &&
    !installState.isInstalled &&
    installState.installMethod !== 'unsupported' &&
    installState.isInstallSupported;
  const shouldShowFullInstallCallout = shouldShowInstallCta && !isCompact;
  const shouldShowCompactInstallAction = shouldShowInstallCta && isCompact;
  const hasCurrentDevicePushSubscription =
    pushState?.currentDeviceSubscription != null;
  const currentDeviceSubscriptionId =
    pushState?.currentDeviceSubscription?.subscription_id ?? null;
  const canShowQueueReminder =
    isQueuedMode &&
    installState !== null &&
    installState.isInstalled &&
    pushState !== null &&
    hasCurrentDevicePushSubscription &&
    pushState.permissionState === 'granted' &&
    currentDeviceSubscriptionId !== null;
  const canShowPushSetup =
    isQueuedMode &&
    installState !== null &&
    installState.isInstalled &&
    pushState !== null &&
    !canShowQueueReminder &&
    (hasCurrentDevicePushSubscription ||
      (pushState.webPushEnabled && pushState.isBrowserSupported));
  useEffect(() => {
    if (mode !== 'offered') {
      return;
    }

    setOfferCountdownNowMs(Date.now());
    const intervalId = window.setInterval(() => {
      setOfferCountdownNowMs(Date.now());
    }, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [mode, offerExpiresAt]);

  const shouldShowFullPushCallout = canShowPushSetup && !isCompact;
  const shouldShowCompactPushAction = canShowPushSetup && isCompact;
  const shouldShowFullQueueReminderCallout = canShowQueueReminder && !isCompact;
  const shouldShowCompactQueueReminderAction = canShowQueueReminder && isCompact;
  const installSummary = installState ? buildInstallSummary(installState) : null;
  const installSheetCopy = installState ? buildInstallSheetCopy(installState) : null;
  const installLauncherLabel = installState ? getInstallLauncherLabel(installState) : null;
  const compactInstallLauncherLabel = installState
    ? getCompactInstallLauncherLabel(installState)
    : null;
  const pushCalloutCopy = pushState ? buildPushCalloutCopy(pushState) : null;
  const pushSheetCopy = pushState ? buildPushSheetCopy(pushState) : null;
  const compactPushLauncherLabel = pushState
    ? getCompactPushLauncherLabel(pushState)
    : null;
  const queueReminderCalloutCopy =
    currentDeviceSubscriptionId !== null
      ? buildQueueReminderCalloutCopy({
          queueReminderState: queueReminderState ?? null,
          currentDeviceSubscriptionId,
        })
      : null;
  const queueReminderSheetCopy =
    currentDeviceSubscriptionId !== null
      ? buildQueueReminderSheetCopy({
          queueReminderState: queueReminderState ?? null,
          currentDeviceSubscriptionId,
        })
      : null;
  const compactQueueReminderLauncherLabel =
    currentDeviceSubscriptionId !== null
      ? getCompactQueueReminderLauncherLabel({
          queueReminderState: queueReminderState ?? null,
          currentDeviceSubscriptionId,
        })
      : null;

  const activeViewportProfile = useMemo(
    () =>
      selectViewportProfile({
        viewportWidthPx: viewportSize.widthPx,
        viewportHeightPx: viewportSize.heightPx,
        mobileMaxWidthPx: MOBILE_MAX_WIDTH_PX,
        tabletMaxWidthPx: TABLET_MAX_WIDTH_PX,
      }),
    [viewportSize.heightPx, viewportSize.widthPx]
  );
  const activeVideoViewportBoundingBox = useMemo(
    () =>
      selectCriticalBoundingBox({
        viewportWidthPx: viewportSize.widthPx,
        viewportHeightPx: viewportSize.heightPx,
        mobileMaxWidthPx: MOBILE_MAX_WIDTH_PX,
        tabletMaxWidthPx: TABLET_MAX_WIDTH_PX,
        criticalBoundingBoxByScreen: OVERHEAD_VIDEO_VIEWPORT.criticalBoundingBoxByScreen,
      }),
    [viewportSize.heightPx, viewportSize.widthPx]
  );
  const activeManualFrameStyle = OVERHEAD_VIDEO_VIEWPORT.manualFrameStyleByScreen[activeViewportProfile];
  const videoStyle = useMemo(
    () => {
      if (activeManualFrameStyle) {
        return computeManualVideoStyle(
          adaptManualFrameStyleToViewport({
            frameStyle: activeManualFrameStyle,
            referenceViewport: OVERHEAD_VIDEO_VIEWPORT.manualFrameReferenceViewport,
            targetViewportWidthPx: viewportSize.widthPx,
            targetViewportHeightPx: viewportSize.heightPx,
          })
        );
      }
      if (usesCompatibilityVideoFraming) {
        return computeManualVideoStyle({
          leftPct: 0,
          topPct: 0,
          widthPct: 100,
          heightPct: 100,
          objectPositionXPct: activeVideoViewportBoundingBox.leftPct + activeVideoViewportBoundingBox.widthPct / 2,
          objectPositionYPct: activeVideoViewportBoundingBox.topPct + activeVideoViewportBoundingBox.heightPct / 2,
        });
      }
      return computeCriticalVideoStyle({
        viewportWidthPx: viewportSize.widthPx,
        viewportHeightPx: viewportSize.heightPx,
        sourceWidthPx: SOURCE_VIDEO_WIDTH_PX,
        sourceHeightPx: SOURCE_VIDEO_HEIGHT_PX,
        criticalBox: activeVideoViewportBoundingBox,
      });
    },
    [
      activeManualFrameStyle,
      activeVideoViewportBoundingBox,
      usesCompatibilityVideoFraming,
      viewportSize.heightPx,
      viewportSize.widthPx,
    ]
  );

  useEffect(() => {
    const containerElement = containerRef.current;
    if (!containerElement) {
      return;
    }
    const updateViewportSize = () => {
      const rect = containerElement.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        setViewportSize({
          widthPx: rect.width,
          heightPx: rect.height,
        });
      }
    };
    const resizeObserver = new ResizeObserver(updateViewportSize);
    resizeObserver.observe(containerElement);
    updateViewportSize();
    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const handleOpenInstallSheet = useCallback(() => {
    if (!shouldShowInstallCta) {
      return;
    }

    setIsInstallSheetOpen(true);
  }, [shouldShowInstallCta]);

  const handleCloseInstallSheet = useCallback(() => {
    setIsInstallSheetOpen(false);
  }, []);

  const handleOpenPushSheet = useCallback(() => {
    if (!canShowPushSetup) {
      return;
    }

    setIsPushSheetOpen(true);
  }, [canShowPushSetup]);

  const handleClosePushSheet = useCallback(() => {
    setIsPushSheetOpen(false);
  }, []);

  const handleOpenQueueReminderSheet = useCallback(() => {
    if (!canShowQueueReminder) {
      return;
    }

    setIsQueueReminderSheetOpen(true);
  }, [canShowQueueReminder]);

  const handleCloseQueueReminderSheet = useCallback(() => {
    setIsQueueReminderSheetOpen(false);
  }, []);

  const handleRequestInstall = useCallback(async () => {
    if (!onRequestInstall) {
      return;
    }

    await onRequestInstall();
    setIsInstallSheetOpen(false);
  }, [onRequestInstall]);

  const handleEnablePushSetup = useCallback(async () => {
    if (!onEnablePushSetup) {
      return;
    }

    await onEnablePushSetup();
  }, [onEnablePushSetup]);

  const handleDisablePushSetup = useCallback(async () => {
    if (!onDisablePushSetup) {
      return;
    }

    await onDisablePushSetup();
  }, [onDisablePushSetup]);

  const handleOpenPushSheetFromReminder = useCallback(() => {
    setIsQueueReminderSheetOpen(false);
    setIsPushSheetOpen(true);
  }, []);

  const handleEnableQueueReminder = useCallback(async () => {
    if (!onEnableQueueReminder) {
      return;
    }

    await onEnableQueueReminder();
  }, [onEnableQueueReminder]);

  const handleDisableQueueReminder = useCallback(async () => {
    if (!onDisableQueueReminder) {
      return;
    }

    await onDisableQueueReminder();
  }, [onDisableQueueReminder]);

  const statPadding = isCompact ? 'p-2.5' : 'p-4';
  const statValue = isCompact ? 'text-lg' : 'text-2xl';
  const statLabel = isCompact ? 'text-[10px]' : 'text-xs';
  const gridLayout = isCompact ? 'grid-cols-2 gap-2' : 'gap-3 sm:grid-cols-2';
  const etaLabel = formatEta(queueInfo?.eta_seconds);
  const shouldShowCompactAuxiliaryAction =
    shouldShowCompactInstallAction ||
    shouldShowCompactPushAction ||
    shouldShowCompactQueueReminderAction;
  const compactCardHeightClassName = shouldShowCompactAuxiliaryAction
    ? 'h-[210px] max-[900px]:h-[184px] max-[600px]:h-[152px]'
    : 'h-[180px] max-[900px]:h-[160px] max-[600px]:h-[126px]';
  const queueViewportPaddingClassName =
    'px-4 pb-[calc(env(safe-area-inset-bottom,0px)+16px)] pt-[calc(env(safe-area-inset-top,0px)+16px)] sm:p-6';
  const fullCardClassName = 'flex max-h-full flex-col overflow-hidden';
  const fullCardHeaderClassName = 'shrink-0 gap-3 max-[600px]:p-5';
  const fullCardTitleClassName = 'text-2xl sm:text-3xl';
  const fullCardDescriptionClassName = 'text-sm leading-6 text-muted-foreground sm:text-base';
  const fullCardContentClassName =
    'min-h-0 space-y-5 overflow-y-auto max-[600px]:px-5 max-[600px]:pb-5';
  const queueStandaloneShellClassName =
    `relative flex h-[var(--app-height)] min-h-[var(--app-height)] w-full items-center justify-center ${queueViewportPaddingClassName}`;
  const queueVideoShellClassName =
    isFirefox
      ? 'firefox-live-video-ui relative h-[var(--app-height)] min-h-[var(--app-height)] w-full'
      : 'relative h-[var(--app-height)] min-h-[var(--app-height)] w-full';
  const queueVideoCardSlotClassName =
    `relative z-10 flex h-full w-full items-end justify-center ${queueViewportPaddingClassName}`;
  const sheetOverlayClassName =
    'absolute inset-0 z-30 flex items-end justify-center overflow-y-auto bg-black/60 px-4 pb-[calc(env(safe-area-inset-bottom,0px)+16px)] pt-[calc(env(safe-area-inset-top,0px)+16px)] backdrop-blur-sm sm:items-center sm:p-4';
  const sheetCardClassName =
    'flex max-h-full w-full max-w-[460px] flex-col overflow-hidden border-white/10 bg-slate-950/95 shadow-2xl';
  const sheetHeaderClassName = 'shrink-0 gap-3 max-[600px]:p-5';
  const sheetContentClassName =
    'min-h-0 space-y-4 overflow-y-auto max-[600px]:px-5 max-[600px]:pb-5';
  const queueStats = mode === 'queued' && (
    <div className={`grid ${gridLayout}`}>
      <div className={`min-w-0 rounded-2xl border border-white/10 bg-white/5 ${statPadding}`}>
        <span className={`mb-1.5 block uppercase tracking-[0.15em] text-muted-foreground ${statLabel}`}>
          Position
        </span>
        <strong className={statValue}>{queueInfo?.position ?? '--'}</strong>
      </div>
      <div className={`min-w-0 rounded-2xl border border-white/10 bg-white/5 ${statPadding}`}>
        <span className={`mb-1.5 block uppercase tracking-[0.15em] text-muted-foreground ${statLabel}`}>
          ETA
        </span>
        <strong className={statValue}>{etaLabel}</strong>
      </div>
    </div>
  );

  const card = (
    <Card
      className={`w-full max-w-[520px] border-white/10 bg-white/5 shadow-2xl backdrop-blur-xl ${
        isCompact
          ? compactCardHeightClassName
          : fullCardClassName
      }`}
    >
      {isCompact ? (
        <CardContent className="flex h-full flex-col justify-between gap-3 p-3 pt-3">
          {queueStats}
          {shouldShowCompactInstallAction ? (
            <div className="rounded-2xl border border-primary/20 bg-primary/10 px-3 py-2.5">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-white">
                    Want queue alerts?
                  </div>
                  <div className="text-[10px] leading-4 text-white/70">
                    Add Play Claw to your home screen first.
                  </div>
                </div>
                <Button size="sm" onClick={handleOpenInstallSheet}>
                  {compactInstallLauncherLabel}
                </Button>
              </div>
            </div>
          ) : null}
          {!shouldShowCompactInstallAction && shouldShowCompactPushAction && pushCalloutCopy ? (
            <div className="rounded-2xl border border-primary/20 bg-primary/10 px-3 py-2.5">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-white">
                    {pushCalloutCopy.title}
                  </div>
                  <div className="text-[10px] leading-4 text-white/70">
                    {pushCalloutCopy.body}
                  </div>
                </div>
                {pushCalloutCopy.launcherLabel ? (
                  <Button
                    size="sm"
                    onClick={handleOpenPushSheet}
                    disabled={pushState?.isBusy || pushState?.isLoading}
                  >
                    {compactPushLauncherLabel}
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}
          {!shouldShowCompactInstallAction &&
          !shouldShowCompactPushAction &&
          shouldShowCompactQueueReminderAction &&
          queueReminderCalloutCopy ? (
            <div className="rounded-2xl border border-primary/20 bg-primary/10 px-3 py-2.5">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-white">
                    {queueReminderCalloutCopy.title}
                  </div>
                  <div className="text-[10px] leading-4 text-white/70">
                    {queueReminderCalloutCopy.body}
                  </div>
                </div>
                {queueReminderCalloutCopy.launcherLabel ? (
                  <Button
                    size="sm"
                    onClick={handleOpenQueueReminderSheet}
                    disabled={Boolean(queueReminderBusy)}
                  >
                    {compactQueueReminderLauncherLabel}
                  </Button>
                ) : null}
              </div>
            </div>
          ) : null}
        </CardContent>
      ) : (
        <>
          <CardHeader className={fullCardHeaderClassName}>
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="w-fit">Teleop</Badge>
              {shouldShowEarlyAccessPilot ? <EarlyAccessPilotBadge /> : null}
            </div>
            <CardTitle className={fullCardTitleClassName}>{title}</CardTitle>
            <CardDescription className={fullCardDescriptionClassName}>
              {subtitle}
            </CardDescription>
          </CardHeader>
          <CardContent className={fullCardContentClassName}>
            {shouldShowEarlyAccessPilot ? (
              <div className="rounded-2xl border border-amber-200/20 bg-amber-300/10 px-4 py-3 text-sm leading-6 text-white/80">
                {EARLY_ACCESS_PILOT_MESSAGE}
              </div>
            ) : null}
            {queueStats}
            {mode === 'queued' && (
              <Button className="w-full" onClick={onAcknowledge}>
                Get In Line
              </Button>
            )}
            {shouldShowFullInstallCallout ? (
              <div className="rounded-2xl border border-primary/20 bg-primary/10 p-4">
                <div className="space-y-3">
                  <div>
                    <div className="text-sm font-semibold text-white">
                      Want to be notified when it&apos;s almost your turn?
                    </div>
                    <div className="mt-1 text-sm leading-6 text-white/75">
                      {installSummary}
                    </div>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="w-full sm:w-auto"
                    onClick={handleOpenInstallSheet}
                  >
                    {installLauncherLabel}
                  </Button>
                </div>
              </div>
            ) : null}
            {!shouldShowFullInstallCallout && shouldShowFullPushCallout && pushCalloutCopy ? (
              <div className="rounded-2xl border border-primary/20 bg-primary/10 p-4">
                <div className="space-y-3">
                  <div>
                    <div className="text-sm font-semibold text-white">
                      {pushCalloutCopy.title}
                    </div>
                    <div className="mt-1 text-sm leading-6 text-white/75">
                      {pushCalloutCopy.body}
                    </div>
                  </div>
                  {pushState?.errorMessage ? (
                    <div className="text-sm text-red-200/90">
                      {pushState.errorMessage}
                    </div>
                  ) : null}
                  {pushCalloutCopy.launcherLabel ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="w-full sm:w-auto"
                      onClick={handleOpenPushSheet}
                      disabled={pushState?.isBusy || pushState?.isLoading}
                    >
                      {pushCalloutCopy.launcherLabel}
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : null}
            {!shouldShowFullInstallCallout &&
            !shouldShowFullPushCallout &&
            shouldShowFullQueueReminderCallout &&
            queueReminderCalloutCopy ? (
              <div className="rounded-2xl border border-primary/20 bg-primary/10 p-4">
                <div className="space-y-3">
                  <div>
                    <div className="text-sm font-semibold text-white">
                      {queueReminderCalloutCopy.title}
                    </div>
                    <div className="mt-1 text-sm leading-6 text-white/75">
                      {queueReminderCalloutCopy.body}
                    </div>
                  </div>
                  {queueReminderErrorMessage ? (
                    <div className="text-sm text-red-200/90">
                      {queueReminderErrorMessage}
                    </div>
                  ) : null}
                  {queueReminderCalloutCopy.launcherLabel ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      className="w-full sm:w-auto"
                      onClick={handleOpenQueueReminderSheet}
                      disabled={Boolean(queueReminderBusy)}
                    >
                      {queueReminderCalloutCopy.launcherLabel}
                    </Button>
                  ) : null}
                </div>
              </div>
            ) : null}
            {mode === 'offered' && (
              <Button className="w-full" onClick={onConfirmReady}>
                I&apos;m Ready
              </Button>
            )}
            {mode === 'error' && (
              <div className="text-sm font-medium text-destructive">
                {errorMessage || 'Please try again.'}
              </div>
            )}
            {mode === 'error' && (
              <Button className="w-full" onClick={onRetry}>
                Retry
              </Button>
            )}
            {mode === 'queued' &&
            !shouldShowFullInstallCallout &&
            !shouldShowFullPushCallout &&
            !shouldShowFullQueueReminderCallout ? (
              <div className="text-sm text-muted-foreground">
                Keep this screen open while you wait in line.
              </div>
            ) : null}
          </CardContent>
        </>
      )}
    </Card>
  );

  const installSheet =
    isInstallSheetOpen && installSheetCopy ? (
      <div className={sheetOverlayClassName}>
        <Card className={sheetCardClassName}>
          <CardHeader className={sheetHeaderClassName}>
            <div className="flex items-center justify-between gap-3">
              <Badge className="w-fit bg-primary/15 text-primary hover:bg-primary/15">
                Queue Alerts
              </Badge>
              <Button variant="ghost" size="sm" onClick={handleCloseInstallSheet}>
                Not now
              </Button>
            </div>
            <CardTitle className="text-2xl">{installSheetCopy.title}</CardTitle>
            <CardDescription className="text-base leading-6 text-white/75">
              {installSheetCopy.body}
            </CardDescription>
          </CardHeader>
          <CardContent className={sheetContentClassName}>
            <div className="space-y-2 rounded-2xl border border-white/10 bg-white/5 p-4">
              {installSheetCopy.steps.map((step, index) => {
                const stepNumber = index + 1;
                return (
                  <div key={step} className="flex items-start gap-3 text-sm text-white/80">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-semibold text-white">
                      {stepNumber}
                    </div>
                    <div className="leading-6">{step}</div>
                  </div>
                );
              })}
            </div>
            {installSheetCopy.primaryActionLabel && onRequestInstall ? (
              <Button className="w-full" onClick={handleRequestInstall}>
                {installSheetCopy.primaryActionLabel}
              </Button>
            ) : null}
          </CardContent>
        </Card>
      </div>
    ) : null;

  const pushSheet =
    isPushSheetOpen && pushSheetCopy ? (
      <div className={sheetOverlayClassName}>
        <Card className={sheetCardClassName}>
          <CardHeader className={sheetHeaderClassName}>
            <div className="flex items-center justify-between gap-3">
              <Badge className="w-fit bg-primary/15 text-primary hover:bg-primary/15">
                Queue Alerts
              </Badge>
              <Button variant="ghost" size="sm" onClick={handleClosePushSheet}>
                Close
              </Button>
            </div>
            <CardTitle className="text-2xl">{pushSheetCopy.title}</CardTitle>
            <CardDescription className="text-base leading-6 text-white/75">
              {pushSheetCopy.body}
            </CardDescription>
          </CardHeader>
          <CardContent className={sheetContentClassName}>
            <div className="space-y-2 rounded-2xl border border-white/10 bg-white/5 p-4">
              {pushSheetCopy.steps.map((step, index) => {
                const stepNumber = index + 1;
                return (
                  <div key={step} className="flex items-start gap-3 text-sm text-white/80">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-semibold text-white">
                      {stepNumber}
                    </div>
                    <div className="leading-6">{step}</div>
                  </div>
                );
              })}
            </div>
            {pushState?.errorMessage ? (
              <div className="text-sm text-red-200/90">
                {pushState.errorMessage}
              </div>
            ) : null}
            {pushSheetCopy.primaryActionLabel &&
            pushSheetCopy.primaryActionKind === 'enable' ? (
              <Button
                className="w-full"
                onClick={handleEnablePushSetup}
                disabled={pushState?.isBusy}
              >
                {pushState?.isBusy ? 'Enabling...' : pushSheetCopy.primaryActionLabel}
              </Button>
            ) : null}
            {pushSheetCopy.primaryActionLabel &&
            pushSheetCopy.primaryActionKind === 'disable' ? (
              <Button
                variant="secondary"
                className="w-full"
                onClick={handleDisablePushSetup}
                disabled={pushState?.isBusy}
              >
                {pushState?.isBusy ? 'Updating...' : pushSheetCopy.primaryActionLabel}
              </Button>
            ) : null}
          </CardContent>
        </Card>
      </div>
    ) : null;

  const queueReminderSheet =
    isQueueReminderSheetOpen && queueReminderSheetCopy ? (
      <div className={sheetOverlayClassName}>
        <Card className={sheetCardClassName}>
          <CardHeader className={sheetHeaderClassName}>
            <div className="flex items-center justify-between gap-3">
              <Badge className="w-fit bg-primary/15 text-primary hover:bg-primary/15">
                Queue Reminder
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCloseQueueReminderSheet}
              >
                Close
              </Button>
            </div>
            <CardTitle className="text-2xl">{queueReminderSheetCopy.title}</CardTitle>
            <CardDescription className="text-base leading-6 text-white/75">
              {queueReminderSheetCopy.body}
            </CardDescription>
          </CardHeader>
          <CardContent className={sheetContentClassName}>
            <div className="space-y-2 rounded-2xl border border-white/10 bg-white/5 p-4">
              {queueReminderSheetCopy.steps.map((step, index) => {
                const stepNumber = index + 1;
                return (
                  <div key={step} className="flex items-start gap-3 text-sm text-white/80">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white/10 text-xs font-semibold text-white">
                      {stepNumber}
                    </div>
                    <div className="leading-6">{step}</div>
                  </div>
                );
              })}
            </div>
            {queueReminderErrorMessage ? (
              <div className="text-sm text-red-200/90">
                {queueReminderErrorMessage}
              </div>
            ) : null}
            {queueReminderSheetCopy.primaryActionLabel &&
            queueReminderSheetCopy.primaryActionKind === 'enable' ? (
              <Button
                className="w-full"
                onClick={handleEnableQueueReminder}
                disabled={Boolean(queueReminderBusy)}
              >
                {queueReminderBusy ? 'Updating...' : queueReminderSheetCopy.primaryActionLabel}
              </Button>
            ) : null}
            {queueReminderSheetCopy.primaryActionLabel &&
            queueReminderSheetCopy.primaryActionKind === 'disable' ? (
              <Button
                variant="secondary"
                className="w-full"
                onClick={handleDisableQueueReminder}
                disabled={Boolean(queueReminderBusy)}
              >
                {queueReminderBusy ? 'Updating...' : queueReminderSheetCopy.primaryActionLabel}
              </Button>
            ) : null}
            <Button
              variant="ghost"
              className="w-full"
              onClick={handleOpenPushSheetFromReminder}
              disabled={pushState?.isBusy}
            >
              Device Setup
            </Button>
          </CardContent>
        </Card>
      </div>
    ) : null;

  if (!roomId) {
    return (
      <div className={queueStandaloneShellClassName}>
        {card}
        {installSheet}
        {pushSheet}
        {queueReminderSheet}
      </div>
    );
  }

  return (
    <div ref={containerRef} className={queueVideoShellClassName}>
      <video
        ref={videoRef}
        className="absolute"
        style={videoStyle}
        autoPlay
        playsInline
        muted
      />
      <div className="absolute inset-0 bg-black/40" />
      <LiveKitOperatorProvider
        roomName={roomId}
        videoRef={videoRef}
        onStatusChange={setLivekitStatus}
      />
      {livekitStatus === 'failed' && (
        <div className="absolute left-4 top-4 rounded-full border border-white/10 bg-black/60 px-3 py-1 text-xs text-white/80">
          Live video unavailable
        </div>
      )}
      <VideoDebugOverlay
        enabled={videoDebugEnabled}
        videoRef={videoRef}
        viewportWidthPx={viewportSize.widthPx}
        viewportHeightPx={viewportSize.heightPx}
        viewportProfile={activeViewportProfile}
        criticalBoundingBox={activeVideoViewportBoundingBox}
        expectedStyle={videoStyle}
      />
      <div className={queueVideoCardSlotClassName}>
        {card}
      </div>
      {installSheet}
      {pushSheet}
      {queueReminderSheet}
    </div>
  );
};

export default QueueScreen;
